/**
 * Change orders in the budget (2026-09-19, migration 0038).
 *
 * THE MODEL — decided with Mick, don't re-litigate:
 *   - The budget the designer typed stays put: it is the ORIGINAL budget.
 *   - REVISED budget = original + every APPROVED change order (work + fee).
 *     Calculated, never stored, so it can't drift and the original survives.
 *   - Approved = status 'acknowledged' (displayed "Approved"). Sent = pending,
 *     shown as "if approved", never counted. Everything else is ignored.
 *   - A change order raises the BUDGET, not the projected cost: the work is
 *     costed as materials and labor, the way it always is, or it would be
 *     counted twice. Its design fee is the exception — fees aren't entered
 *     anywhere else — so an approved fee also counts as a projected cost.
 *
 * Pure functions, no Supabase, no DOM: money math, testable to the cent.
 */

import type { ChangeOrderRow } from '@/types/database';
import { fmtMoney } from '@/lib/dom';

export type ChangeOrderMoney = Pick<ChangeOrderRow, 'status' | 'cost_delta_cents' | 'designer_fee_cents'>;

export const isApproved = (co: Pick<ChangeOrderRow, 'status'>): boolean => co.status === 'acknowledged';
export const isPending = (co: Pick<ChangeOrderRow, 'status'>): boolean => co.status === 'sent';

/** Work plus fee: what the client is agreeing to pay for this change. */
export function changeOrderTotal(co: Pick<ChangeOrderRow, 'cost_delta_cents' | 'designer_fee_cents'>): number {
  return (co.cost_delta_cents ?? 0) + (co.designer_fee_cents ?? 0);
}

export interface ChangeOrderTotals {
  approvedCount: number;
  approvedWorkCents: number;
  approvedFeeCents: number;
  /** work + fee — what the revised budget adds. */
  approvedTotalCents: number;
  pendingCount: number;
  /** work + fee of change orders awaiting the client. Never counted. */
  pendingTotalCents: number;
}

export function summarizeChangeOrders(cos: readonly ChangeOrderMoney[]): ChangeOrderTotals {
  const t: ChangeOrderTotals = {
    approvedCount: 0, approvedWorkCents: 0, approvedFeeCents: 0, approvedTotalCents: 0,
    pendingCount: 0, pendingTotalCents: 0,
  };
  for (const co of cos) {
    if (isApproved(co)) {
      t.approvedCount++;
      t.approvedWorkCents += co.cost_delta_cents ?? 0;
      t.approvedFeeCents += co.designer_fee_cents ?? 0;
    } else if (isPending(co)) {
      t.pendingCount++;
      t.pendingTotalCents += changeOrderTotal(co);
    }
  }
  t.approvedTotalCents = t.approvedWorkCents + t.approvedFeeCents;
  return t;
}

/**
 * Original + approved changes. Null when no original budget is set: approved
 * changes on an unknown budget don't make a known one.
 */
export function revisedBudgetCents(originalCents: number | null, totals: ChangeOrderTotals): number | null {
  return originalCents == null ? null : originalCents + totals.approvedTotalCents;
}

/** Change-order totals per project, for lists that show many budgets at once. */
export function totalsByProject(
  cos: readonly (ChangeOrderMoney & { project_id: string })[],
): Map<string, ChangeOrderTotals> {
  const byProject = new Map<string, ChangeOrderMoney[]>();
  for (const co of cos) {
    const list = byProject.get(co.project_id);
    if (list) list.push(co); else byProject.set(co.project_id, [co]);
  }
  return new Map([...byProject].map(([id, list]) => [id, summarizeChangeOrders(list)]));
}

// ─── Rooms ──────────────────────────────────────────────────────────────────

export interface RoomChangeOrders<T> {
  /** Change orders for this room alone — their amounts belong to it. */
  direct: T[];
  /**
   * Change orders covering this room and others, or the whole project (no
   * rooms named). Listed, never added to the room: splitting one amount
   * across rooms by guesswork would make every room's figure quietly wrong.
   */
  shared: T[];
  /** Approved work + fee of `direct` only. */
  directApprovedCents: number;
}

export function roomChangeOrders<T extends Pick<ChangeOrderRow,
  'status' | 'cost_delta_cents' | 'designer_fee_cents' | 'affected_room_ids'>>(
  cos: readonly T[], roomId: string,
): RoomChangeOrders<T> {
  const direct: T[] = [];
  const shared: T[] = [];
  for (const co of cos) {
    if (co.status === 'draft' || co.status === 'cancelled') continue;
    const rooms = co.affected_room_ids ?? [];
    if (rooms.length === 1 && rooms[0] === roomId) direct.push(co);
    else if (rooms.length === 0 || rooms.includes(roomId)) shared.push(co);
  }
  const directApprovedCents = direct.filter(isApproved).reduce((s, co) => s + changeOrderTotal(co), 0);
  return { direct, shared, directApprovedCents };
}

// ─── "What's included", under each figure ───────────────────────────────────

const signed = (c: number): string => `${c < 0 ? '−' : '+'}${fmtMoney(Math.abs(c))}`;
const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export interface BudgetExplanations {
  original: string;
  approvedChanges: string;
  revised: string;
  projected: string;
  variance: string;
  /** Null when nothing is awaiting the client. */
  pending: string | null;
}

/**
 * One short line per budget figure, saying what's in it. Mick asked for this
 * explicitly: once change orders move the numbers, each figure has to say
 * what it's made of.
 */
export function explainBudget(input: {
  originalCents: number | null;
  totals: ChangeOrderTotals;
  materialsCents: number;
  laborCents: number;
  baseFeeCents: number;
}): BudgetExplanations {
  const { originalCents, totals: t, materialsCents, laborCents, baseFeeCents } = input;
  const fees = baseFeeCents + t.approvedFeeCents;
  return {
    original: originalCents == null ? 'Not set yet — enter the agreed budget' : 'As agreed at the start of the project',
    approvedChanges: t.approvedCount === 0
      ? 'No approved change orders yet'
      : `${plural(t.approvedCount, 'approved change order', 'approved change orders')}: work ${signed(t.approvedWorkCents)}`
        + (t.approvedFeeCents !== 0 ? `, design fees ${signed(t.approvedFeeCents)}` : ''),
    revised: originalCents == null
      ? 'Set an original budget to see the revised one'
      : t.approvedCount === 0 ? 'Same as the original — nothing approved yet' : 'Original budget + approved change orders',
    projected: `Materials ${fmtMoney(materialsCents)} · labor ${fmtMoney(laborCents)} · design fees ${fmtMoney(fees)}`
      + (t.approvedFeeCents !== 0 ? ` (incl. ${fmtMoney(t.approvedFeeCents)} from change orders)` : ''),
    variance: originalCents == null ? 'Needs a budget to compare against' : 'Revised budget − projected',
    pending: t.pendingCount === 0
      ? null
      : `${plural(t.pendingCount, 'change order', 'change orders')} awaiting the client: ${signed(t.pendingTotalCents)} if approved — not counted yet`,
  };
}
